import { flexRender, getCoreRowModel, RowSelectionState, Table, useReactTable } from '@tanstack/react-table'
import classNames from 'classnames'
import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { Routes } from '../../routes'
import { useGetRepositories } from '../../utils/api-client/api-hooks'
import { type Repository } from '../../utils/api-client/api-schemas'
import { type getEnvironmentData } from '../../utils/hooks/get-environment-data'
import { useIsStalePlaceholderData } from '../../utils/query-utils'
import { DefaultButton } from '../buttons/default-button'
import { SearchInput } from '../input/search-input'
import { PaginationControls } from '../table/pagination-controls'
import { isInteractiveElementClick } from '../table/row-interaction'
import { type Pagination } from '../table/table'
import { TableBodySkeleton } from '../table/table-body-skeleton'
import {
  useRepositoriesTableColumnDefinitions,
  type UseRepositoriesTableColumnDefinitionsOptions,
} from './repositories-table.columns'
import * as styles from './repositories-table.css'

type GetRepositoriesQueryResult = Pick<
  ReturnType<typeof useGetRepositories>,
  'data' | 'isLoading' | 'isPlaceholderData'
>

// #region Impure Component (handles data fetching)
type RepositoriesTableProps = Omit<RepositoriesTablePureProps, 'getRepositoriesQueryResult'>

export const RepositoriesTable: React.FC<RepositoriesTableProps> = ({ pagination, searchTerm, ...rest }) => {
  const getRepositoriesQueryResult = useGetRepositories({
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    searchTerm,
  })

  return (
    <RepositoriesTablePure
      getRepositoriesQueryResult={getRepositoriesQueryResult}
      pagination={pagination}
      searchTerm={searchTerm}
      {...rest}
    />
  )
}
// #endregion Impure Component

// #region Pure Component (for testing/stories)
type RepositoriesTablePureProps = RepositoriesTableControlsProps &
  RepositoriesReactTableProps &
  RepositoriesTablePaginationControlsProps

export const RepositoriesTablePure: React.FC<RepositoriesTablePureProps> = ({
  columnDefinitionOptions,
  getRepositoriesQueryResult,
  pagination,
  onPaginationChange,
  environmentData,
  onOpenAddRepository,
  searchTerm,
  onChangeSearchTerm,
}) => {
  return (
    <>
      <RepositoriesTableControls
        getRepositoriesQueryResult={getRepositoriesQueryResult}
        environmentData={environmentData}
        onOpenAddRepository={onOpenAddRepository}
        searchTerm={searchTerm}
        onChangeSearchTerm={onChangeSearchTerm}
      />

      <RepositoriesReactTable
        columnDefinitionOptions={columnDefinitionOptions}
        getRepositoriesQueryResult={getRepositoriesQueryResult}
        searchTerm={searchTerm}
        onOpenAddRepository={onOpenAddRepository}
      />

      {!getRepositoriesQueryResult.isLoading && (
        <RepositoriesTablePaginationControls
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          getRepositoriesQueryResult={getRepositoriesQueryResult}
        />
      )}
    </>
  )
}
// #endregion Pure Component

// #region Repositories Table Controls
type RepositoriesTableControlsProps = {
  getRepositoriesQueryResult: GetRepositoriesQueryResult
  environmentData: ReturnType<typeof getEnvironmentData>
  onOpenAddRepository: () => unknown
  searchTerm: string
  onChangeSearchTerm: (value: string) => unknown
}

const RepositoriesTableControls: React.FC<RepositoriesTableControlsProps> = ({
  getRepositoriesQueryResult,
  environmentData,
  onOpenAddRepository,
  searchTerm,
  onChangeSearchTerm,
}) => {
  return (
    <div className={styles.tableControls}>
      <div>
        <h4 className={styles.tableHeading}>
          Repositories{' '}
          {!getRepositoriesQueryResult.isLoading && (
            <span className={styles.installationsCount}>({getRepositoriesQueryResult.data?.total ?? 0})</span>
          )}
        </h4>
        <p className={styles.tableDescription}>
          Locations where {environmentData.githubAppName} has been configured for analysis
        </p>
      </div>

      <div className={styles.tableControlsRight}>
        <SearchInput
          value={searchTerm}
          onChange={value => onChangeSearchTerm(value)}
          size="small"
          debounce={300}
          name="Search repositories"
        />
        <DefaultButton size="small" onClick={onOpenAddRepository}>
          + &nbsp; Connect new
        </DefaultButton>
      </div>
    </div>
  )
}
// #endregion Repositories Table Controls

// #region Repositories React Table
type RepositoriesReactTableProps = Omit<RepositoriesTableHeaderProps & RepositoriesTableBodyProps, 'table'> & {
  columnDefinitionOptions: UseRepositoriesTableColumnDefinitionsOptions
}

const RepositoriesReactTable: React.FC<RepositoriesReactTableProps> = ({
  columnDefinitionOptions,
  getRepositoriesQueryResult,
  searchTerm,
  onOpenAddRepository,
}) => {
  const columns = useRepositoriesTableColumnDefinitions(columnDefinitionOptions)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const table = useReactTable({
    getRowId: data => data.id,
    data: getRepositoriesQueryResult.data?._embedded.items ?? [],
    columns,
    enableRowSelection: true,
    enableGlobalFilter: false,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  })

  return (
    <table className={styles.table}>
      <RepositoriesTableHeader table={table} />
      <RepositoriesTableBody
        table={table}
        getRepositoriesQueryResult={getRepositoriesQueryResult}
        searchTerm={searchTerm}
        onOpenAddRepository={onOpenAddRepository}
      />
    </table>
  )
}

type RepositoriesTableHeaderProps = {
  table: Table<Repository>
}

const RepositoriesTableHeader: React.FC<RepositoriesTableHeaderProps> = ({ table }) => {
  return (
    <thead className={styles.tableHead}>
      {table.getHeaderGroups().map(headerGroup => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map(header => {
            return (
              <th
                key={header.id}
                className={classNames(styles.columnHeader, styles.cell, styles.installationsTableCell)}
              >
                {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            )
          })}
        </tr>
      ))}
    </thead>
  )
}

type RepositoriesTableBodyProps = {
  table: Table<Repository>
  getRepositoriesQueryResult: GetRepositoriesQueryResult
  searchTerm: string
  onOpenAddRepository: () => unknown
}

const RepositoriesTableBody: React.FC<RepositoriesTableBodyProps> = ({
  table,
  getRepositoriesQueryResult: { isPlaceholderData, isLoading },
  searchTerm,
  onOpenAddRepository,
}) => {
  const isStalePlaceholderData = useIsStalePlaceholderData(isPlaceholderData)
  const navigate = useNavigate()

  if (isLoading || isStalePlaceholderData) {
    return <TableBodySkeleton rowCount={7} columnCount={table.getAllLeafColumns().length} rowHeightInPixels={43} />
  }

  const navigateToRepository = (repositoryId: string, event: React.SyntheticEvent) => {
    // Interactive cell content (name link, action buttons, upload link) handles its own click/navigation.
    if (isInteractiveElementClick(event)) {
      return
    }
    navigate(Routes.RepositoryDetailsPage.createPath({ repositoryId }))
  }

  return (
    <tbody>
      {table.getRowModel().rows?.length ? (
        table.getRowModel().rows.map(row => (
          <tr
            key={row.id}
            className={classNames(styles.row, styles.selectableRow)}
            tabIndex={0}
            onClick={event => navigateToRepository(row.original.id, event)}
            onKeyDown={event => {
              // Only intercept Enter/Space for rows; let interactive descendants keep
              // their native key handling (checked before preventDefault).
              if (event.key !== 'Enter' && event.key !== ' ') {
                return
              }
              if (isInteractiveElementClick(event)) {
                return
              }
              event.preventDefault()
              navigateToRepository(row.original.id, event)
            }}
          >
            {row.getVisibleCells().map(cell => (
              <td key={cell.id} className={classNames(styles.cell, styles.installationsTableCell)}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))
      ) : (
        <tr>
          <td className={styles.emptyStateCell} colSpan={table.getAllLeafColumns().length}>
            <div className={styles.emptyStateContainer}>
              {searchTerm ? (
                <div className={styles.emptyStateText}>No repositories match your search</div>
              ) : (
                <>
                  <div className={styles.emptyStateText}>No repositories</div>
                  <div className={styles.emptyStateSubtext}>
                    <button type="button" className={styles.emptyStateLink} onClick={onOpenAddRepository}>
                      Connect
                    </button>{' '}
                    using your preferred method
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </tbody>
  )
}
// #endregion Repositories React Table

// #region Repositories Table Pagination Controls
type RepositoriesTablePaginationControlsProps = {
  pagination: Pagination
  onPaginationChange: (pagination: Pagination) => unknown
  getRepositoriesQueryResult: GetRepositoriesQueryResult
}

const RepositoriesTablePaginationControls: React.FC<RepositoriesTablePaginationControlsProps> = ({
  pagination,
  onPaginationChange,
  getRepositoriesQueryResult,
}) => {
  return (
    <div className={styles.tableFooter}>
      <PaginationControls
        tableName="Installations"
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        totalItems={getRepositoriesQueryResult.data?.total ?? 0}
        onPageIndexChange={pageIndex => onPaginationChange({ pageIndex, pageSize: pagination.pageSize })}
        onPageSizeChange={pageSize => onPaginationChange({ pageIndex: pagination.pageIndex, pageSize })}
      />
    </div>
  )
}
// #endregion Repositories Table Pagination Controls
